import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import type {
  CloudflareIdentityProvider,
  CloudflareScimConnection,
  CloudflareScimIdentityUpdateBehavior,
} from "../shared/types";
import {
  optimisticallyUpdateCloudflareOverview,
  useAdminApi,
  useRefreshCloudflare,
} from "./admin-query";
import { formatDate } from "./admin-model";
import { errorMessage } from "./api";
import { Modal } from "./Modal";

interface ScimSettingsResponse {
  identityProvider: CloudflareIdentityProvider;
  connection?: CloudflareScimConnection;
  credentialCaptured?: boolean;
}

interface ScimRevealResponse {
  connection: CloudflareScimConnection;
  token: string;
}

export function CloudflareScimDialog({
  identityProvider,
  connection,
  onClose,
  onReveal,
}: {
  identityProvider: CloudflareIdentityProvider;
  connection?: CloudflareScimConnection;
  onClose: () => void;
  onReveal: (secret: { endpoint: string; token: string }) => void;
}) {
  const queryClient = useQueryClient();
  const adminApi = useAdminApi();
  const refresh = useRefreshCloudflare();
  const [enabled, setEnabled] = useState(identityProvider.scim.enabled);
  const [userDeprovision, setUserDeprovision] = useState(
    identityProvider.scim.userDeprovision,
  );
  const [seatDeprovision, setSeatDeprovision] = useState(
    identityProvider.scim.seatDeprovision,
  );
  const [identityUpdateBehavior, setIdentityUpdateBehavior] =
    useState<CloudflareScimIdentityUpdateBehavior>(
      identityProvider.scim.identityUpdateBehavior,
    );
  const [endpoint, setEndpoint] = useState(connection?.endpoint ?? "");
  const [token, setToken] = useState("");
  const [savedMessage, setSavedMessage] = useState("");

  const basePath = `/api/admin/cloudflare/identity-providers/${encodeURIComponent(identityProvider.id)}/scim`;

  const settingsMutation = useMutation({
    mutationFn: () =>
      adminApi<ScimSettingsResponse>(`${basePath}/settings`, {
        method: "PUT",
        body: JSON.stringify({
          confirmation: identityProvider.id,
          enabled,
          identityUpdateBehavior,
          userDeprovision,
          seatDeprovision: userDeprovision && seatDeprovision,
        }),
      }),
    onSuccess: async (result) => {
      optimisticallyUpdateCloudflareOverview(queryClient, {
        type: "identity-provider-upsert",
        identityProvider: result.identityProvider,
      });
      if (result.connection) {
        optimisticallyUpdateCloudflareOverview(queryClient, {
          type: "scim-connection-upsert",
          connection: result.connection,
        });
        setEndpoint(result.connection.endpoint);
      }
      setSavedMessage(
        result.credentialCaptured
          ? "Settings saved. Cloudflare's one-time credential was encrypted automatically."
          : "SCIM settings saved in Cloudflare.",
      );
      await refresh();
    },
  });

  const credentialMutation = useMutation({
    mutationFn: () =>
      adminApi<{ connection: CloudflareScimConnection }>(
        `${basePath}/credential`,
        {
          method: "PUT",
          body: JSON.stringify({
            confirmation: identityProvider.id,
            endpoint: endpoint.trim(),
            token: token.trim(),
          }),
        },
      ),
    onSuccess: async (result) => {
      optimisticallyUpdateCloudflareOverview(queryClient, {
        type: "scim-connection-upsert",
        connection: result.connection,
      });
      setToken("");
      setSavedMessage("SCIM credential encrypted and stored.");
      await refresh();
    },
  });

  const revealMutation = useMutation({
    mutationFn: () =>
      adminApi<ScimRevealResponse>(`${basePath}/reveal`, {
        method: "POST",
        body: JSON.stringify({
          confirmation: identityProvider.id,
          expectedRevision: connection?.revision,
        }),
      }),
    onSuccess: (result) => {
      onClose();
      onReveal({ endpoint: result.connection.endpoint, token: result.token });
    },
  });

  const busy =
    settingsMutation.isPending ||
    credentialMutation.isPending ||
    revealMutation.isPending;
  const error =
    (settingsMutation.error &&
      errorMessage(
        settingsMutation.error,
        "The SCIM settings could not be saved.",
      )) ||
    (credentialMutation.error &&
      errorMessage(
        credentialMutation.error,
        "The SCIM credential could not be stored.",
      )) ||
    (revealMutation.error &&
      errorMessage(
        revealMutation.error,
        "The SCIM token could not be revealed.",
      )) ||
    "";

  function saveSettings(event: FormEvent) {
    event.preventDefault();
    setSavedMessage("");
    settingsMutation.mutate();
  }

  function saveCredential(event: FormEvent) {
    event.preventDefault();
    setSavedMessage("");
    credentialMutation.mutate();
  }

  return (
    <Modal
      closeDisabled={busy}
      eyebrow="Cloudflare identity provider"
      onClose={onClose}
      title={`SCIM · ${identityProvider.name}`}
      titleId="cloudflare-scim-dialog-title"
      wide
    >
      <div className="dialog__body scim-config-dialog">
        <form className="access-config-section" onSubmit={saveSettings}>
          <div className="access-policy-header">
            <div>
              <span className="eyebrow">Provisioning policy</span>
              <p>
                These settings are written directly to this Cloudflare OIDC
                identity provider.
              </p>
            </div>
            <button
              className="button button--primary button--compact"
              disabled={busy}
              type="submit"
            >
              {settingsMutation.isPending ? "Saving…" : "Save settings"}
            </button>
          </div>
          <div className="scim-settings-grid">
            <label className="checkbox-line form-grid__wide">
              <input
                checked={enabled}
                data-autofocus
                disabled={busy}
                onChange={(event) => setEnabled(event.target.checked)}
                type="checkbox"
              />
              <span>
                <strong>Enable SCIM</strong>
                <small>
                  Synchronize users and groups with Cloudflare Access.
                </small>
              </span>
            </label>
            <label>
              <span>Identity update behavior</span>
              <select
                disabled={busy || !enabled}
                onChange={(event) =>
                  setIdentityUpdateBehavior(
                    event.target.value as CloudflareScimIdentityUpdateBehavior,
                  )
                }
                value={identityUpdateBehavior}
              >
                <option value="reauth">Require reauthentication</option>
                <option value="automatic">Automatic identity updates</option>
                <option value="no_action">No action</option>
              </select>
              <small>
                Reauthentication is safer when group claims control access.
              </small>
            </label>
            <div className="scim-dependent-settings">
              <label className="checkbox-line">
                <input
                  checked={userDeprovision}
                  disabled={busy || !enabled}
                  onChange={(event) => {
                    setUserDeprovision(event.target.checked);
                    if (!event.target.checked) setSeatDeprovision(false);
                  }}
                  type="checkbox"
                />
                <span>
                  <strong>Deprovision users</strong>
                  <small>
                    Revoke Access and device sessions when disabled.
                  </small>
                </span>
              </label>
              <label className="checkbox-line">
                <input
                  checked={userDeprovision && seatDeprovision}
                  disabled={busy || !enabled || !userDeprovision}
                  onChange={(event) => setSeatDeprovision(event.target.checked)}
                  type="checkbox"
                />
                <span>
                  <strong>Remove Access seat</strong>
                  <small>Release the billed seat after deprovisioning.</small>
                </span>
              </label>
            </div>
          </div>
        </form>

        <form className="access-config-section" onSubmit={saveCredential}>
          <div className="access-policy-header">
            <div>
              <span className="eyebrow">Stored credential</span>
              <p>
                Import the one-time endpoint and token from Cloudflare. The
                token is encrypted before storage.
              </p>
            </div>
            <span
              className={`panel-status panel-status--${connection ? "positive" : "warning"}`}
            >
              {connection
                ? `${connection.source} · revision ${connection.revision}`
                : "Not stored"}
            </span>
          </div>
          {connection && (
            <div className="scim-credential-summary">
              <code>{connection.endpoint}</code>
              <small>
                Updated {formatDate(connection.updatedAt)} · token never appears
                in overview responses.
              </small>
            </div>
          )}
          <div className="form-grid">
            <label className="form-grid__wide">
              <span>SCIM endpoint</span>
              <input
                disabled={busy}
                onChange={(event) => setEndpoint(event.target.value)}
                placeholder={`https://team.cloudflareaccess.com/scim/${identityProvider.id}`}
                required
                type="url"
                value={endpoint}
              />
              <small>
                Must use this account's cloudflareaccess.com origin and this
                provider ID.
              </small>
            </label>
            <label className="form-grid__wide">
              <span>
                {connection ? "Replacement SCIM token" : "SCIM token"}
              </span>
              <input
                autoComplete="new-password"
                disabled={busy}
                minLength={32}
                onChange={(event) => setToken(event.target.value)}
                placeholder="Paste the one-time token"
                required
                type="password"
                value={token}
              />
            </label>
          </div>
          <div className="scim-credential-actions">
            <a
              className="button button--quiet button--compact"
              href="https://one.dash.cloudflare.com/"
              rel="noreferrer"
              target="_blank"
            >
              Regenerate in Cloudflare ↗
            </a>
            {connection?.revealable && (
              <button
                className="button button--secondary button--compact"
                disabled={busy}
                onClick={() => revealMutation.mutate()}
                type="button"
              >
                {revealMutation.isPending ? "Revealing…" : "Reveal token"}
              </button>
            )}
            <button
              className="button button--primary button--compact"
              disabled={busy || !token.trim() || !endpoint.trim()}
              type="submit"
            >
              {credentialMutation.isPending
                ? "Encrypting…"
                : connection
                  ? "Replace credential"
                  : "Store credential"}
            </button>
          </div>
        </form>

        {savedMessage && (
          <div className="notice notice--success" role="status">
            {savedMessage}
          </div>
        )}
        {error && (
          <div className="notice notice--error" role="alert">
            {error}
          </div>
        )}
      </div>
      <footer className="dialog__actions">
        <span className="dialog__action-note">
          Provider ID: {identityProvider.id}
        </span>
        <button
          className="button button--secondary"
          disabled={busy}
          onClick={onClose}
          type="button"
        >
          Close
        </button>
      </footer>
    </Modal>
  );
}
